import { DocumentUri, InlineCompletionContext, WorkspaceFolder } from 'vscode-languageserver';
import { Position, TextDocument } from 'vscode-languageserver-textdocument';
import { createInterfaceId, Injectable } from '@gitlab-org/di';
import { LsTextDocuments } from './external_interfaces';
import { sanitizeRange } from './utils/sanitize_range';
import { SecretRedactor } from './secret_redaction';

// FIXME: rename this to FileAndCursor or FileAndPosition
export interface IDocContext {
  /**
   * The text before the cursor.
   */
  prefix: string;
  /**
   * The text after the cursor.
   */
  suffix: string;
  /**
   * This is most likely path to a file relative to the workspace
   *  but if the file doesn't belong to a workspace, this field is identical to document URI
   *
   * Example: If the workspace root is `/home/user/workspace`
   * and the file is `/home/user/workspace/src/file.txt`,
   * then the filename is `src/file.txt`.
   */
  fileRelativePath: string;
  position: Position;
  /**
   * The URI of the document.
   */
  uri: DocumentUri;
  /**
   * languageId of the document
   * @readonly
   */
  languageId: TextDocument['languageId'];
  /**
   * The workspace folder that the document belongs to.
   */
  workspaceFolder?: WorkspaceFolder;
}

export interface IDocTransformer {
  transform(context: IDocContext): IDocContext;
}

const getMatchingWorkspaceFolder = (
  fileUri: DocumentUri,
  workspaceFolders: WorkspaceFolder[],
): WorkspaceFolder | undefined => workspaceFolders.find((wf) => fileUri.startsWith(wf.uri));

const getRelativePath = (fileUri: DocumentUri, workspaceFolder?: WorkspaceFolder): string => {
  if (!workspaceFolder) {
    // splitting a string will produce at least one item and so the pop can't return undefined
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return fileUri.split(/[\\/]/).pop()!;
  }
  return fileUri.slice(workspaceFolder.uri.length).replace(/^\//, '');
};

export interface DocumentTransformerService {
  get(uri: string): TextDocument | undefined;
  getContext(
    uri: string,
    position: Position,
    workspaceFolders: WorkspaceFolder[],
    completionContext?: InlineCompletionContext,
  ): IDocContext | undefined;
  transform(context: IDocContext): IDocContext;
}

export const DocumentTransformerService = createInterfaceId<DocumentTransformerService>(
  'DocumentTransformerService',
);
@Injectable(DocumentTransformerService, [LsTextDocuments, SecretRedactor])
export class DefaultDocumentTransformerService implements DocumentTransformerService {
  #transformers: IDocTransformer[] = [];

  #documents: LsTextDocuments;

  constructor(documents: LsTextDocuments, secretRedactor: SecretRedactor) {
    this.#documents = documents;
    this.#transformers.push(secretRedactor);
  }

  get(uri: string) {
    return this.#documents.get(uri);
  }

  getContext(
    uri: string,
    position: Position,
    workspaceFolders: WorkspaceFolder[],
    completionContext?: InlineCompletionContext,
  ): IDocContext | undefined {
    const doc = this.get(uri);
    if (doc === undefined) {
      return undefined;
    }
    return this.transform(getDocContext(doc, position, workspaceFolders, completionContext));
  }

  transform(context: IDocContext): IDocContext {
    return this.#transformers.reduce((ctx, transformer) => transformer.transform(ctx), context);
  }
}

export function getDocContext(
  document: TextDocument,
  position: Position,
  workspaceFolders: WorkspaceFolder[],
  completionContext?: InlineCompletionContext,
): IDocContext {
  let prefix: string;

  if (completionContext?.selectedCompletionInfo) {
    const { selectedCompletionInfo } = completionContext;
    const range = sanitizeRange(selectedCompletionInfo.range);

    prefix = `${document.getText({
      start: document.positionAt(0),
      end: range.start,
    })}${selectedCompletionInfo.text}`;
  } else {
    prefix = document.getText({ start: document.positionAt(0), end: position });
  }

  const suffix = document.getText({
    start: position,
    end: document.positionAt(document.getText().length),
  });

  const workspaceFolder = getMatchingWorkspaceFolder(document.uri, workspaceFolders);
  const fileRelativePath = getRelativePath(document.uri, workspaceFolder);

  return {
    prefix,
    suffix,
    fileRelativePath,
    position,
    uri: document.uri,
    languageId: document.languageId,
    workspaceFolder,
  };
}
